import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { createUser } from '../../api/users';
import { listRoles } from '../../api/roles';
import { pushToast } from '../../components/ui/toastBus';

export default function UserFormModal({ open, onClose }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', password_confirmation: '' });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [errors, setErrors] = useState({});

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles, enabled: open });

  const mutation = useMutation({
    mutationFn: () => createUser({ ...form, roles: selectedRoles }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'User created.' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleClose();
    },
    onError: (err) => {
      setErrors(err.response?.data?.errors || {});
    },
  });

  const handleClose = () => {
    setForm({ name: '', email: '', phone: '', password: '', password_confirmation: '' });
    setSelectedRoles([]);
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      setErrors({ password: ['Passwords do not match.'] });
      return;
    }
    mutation.mutate();
  };

  const toggleRole = (name) => {
    setSelectedRoles((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));
  };

  const fieldError = (field) => errors[field]?.[0];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add user"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Create user
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldError('name')}
        />
        <Input
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          error={fieldError('email')}
        />
        <Input
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          error={fieldError('phone')}
        />
        <Input
          label="Password"
          type="password"
          required
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          error={fieldError('password')}
        />
        <Input
          label="Confirm password"
          type="password"
          required
          value={form.password_confirmation}
          onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
        />

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-1">Roles</p>
          <div className="space-y-1.5">
            {(rolesQuery.data || []).map((role) => (
              <label key={role.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-primary"
                  checked={selectedRoles.includes(role.name)}
                  onChange={() => toggleRole(role.name)}
                />
                {role.name}
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
